import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import connectivityService from '../../services/connectivityService';
import assetPreloader from '../../services/assetPreloader';
import OfflineFallback from '../OfflineFallback';
import './styles.css';

/**
 * Componente wrapper para gerenciar o estado offline/online com otimização para mobile
 * 
 * Este componente:
 * 1. Verifica a disponibilidade do conteúdo atual quando offline
 * 2. Gerencia estratégias de pré-carregamento para economizar dados móveis
 * 3. Oferece feedback visual adequado para dispositivos móveis
 * 4. Implementa detecção de rede móvel vs. Wi-Fi para otimização
 */
function OfflineAwareWrapper({ children, routes = [] }) {
  const location = useLocation();
  const [connectionState, setConnectionState] = useState(connectivityService.getConnectionState());
  const [contentAvailable, setContentAvailable] = useState(true);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationType, setNotificationType] = useState('info'); // 'info', 'warning', 'error'
  
  // Referência para rastrear a última notificação de conectividade
  const lastConnectivityNotification = useRef({
    timestamp: 0, // timestamp da última notificação
    type: null,   // tipo da última notificação
    online: connectivityService.getConnectionState().online // estado online anterior
  });
  
  // Referência para armazenar a configuração de exibição de notificações online
  const userPreferences = useRef({
    showOnlineNotifications: localStorage.getItem('showOnlineNotifications') !== 'false'
  });

  // Função para mostrar notificação temporária
  const showTemporaryNotification = useCallback((message, type = 'info', duration = 3000) => {
    // Verificar se é uma notificação online e se as notificações online estão desativadas
    if (type === 'info' && message.includes('online') && !userPreferences.current.showOnlineNotifications) {
      return; // Não mostrar notificações de online se estiverem desativadas
    }
    
    // Verificar se essa mesma notificação foi mostrada recentemente (últimos 5 segundos)
    const now = Date.now();
    if (
      lastConnectivityNotification.current.type === type &&
      now - lastConnectivityNotification.current.timestamp < 5000 &&
      type === 'info' && message.includes('online')
    ) {
      return; // Não mostrar a mesma notificação repetidamente em intervalos curtos
    }
    
    // Atualizar rastreamento de última notificação
    lastConnectivityNotification.current = {
      timestamp: now,
      type,
      message
    };
    
    setNotificationMessage(message);
    setNotificationType(type);
    setShowNotification(true);
    
    // Esconder após o tempo definido
    setTimeout(() => {
      setShowNotification(false);
    }, duration);
  }, []);
  
  // Alternar exibição de notificações online
  const toggleOnlineNotifications = useCallback(() => {
    const newValue = !userPreferences.current.showOnlineNotifications;
    userPreferences.current.showOnlineNotifications = newValue;
    
    // Persistir a configuração
    localStorage.setItem('showOnlineNotifications', newValue);
    
    showTemporaryNotification(
      `Notificações de status online ${newValue ? 'ativadas' : 'desativadas'}`,
      'info',
      2000
    );
  }, [showTemporaryNotification]);

  // Verificar disponibilidade do conteúdo atual
  const checkContentAvailability = useCallback(async () => {
    try {
      // Obter status de cache para a rota atual
      const routeStatus = assetPreloader.getRouteAssetsStatus(location.pathname);
      
      // Conteúdo está disponível se tivermos pelo menos 90% dos assets
      const isAvailable = routeStatus.cachePercentage >= 90;
      
      // Conteúdo parcialmente disponível (entre 50% e 90%)
      const isPartiallyAvailable = routeStatus.cachePercentage >= 50 && routeStatus.cachePercentage < 90;
      
      setContentAvailable(isAvailable);
      
      // Se o conteúdo estiver apenas parcialmente disponível e estamos offline,
      // mostrar uma notificação
      if (!connectionState.online && isPartiallyAvailable) {
        showTemporaryNotification(
          'Alguns recursos podem não estar disponíveis offline.', 
          'warning'
        );
      }
      
      // Se o conteúdo não estiver disponível e estamos offline,
      // pré-carregar assets para rotas alternativas
      if (!isAvailable && !connectionState.online) {
        preloadAlternativeRoutes();
      }
    } catch (error) {
      console.error('Erro ao verificar disponibilidade de conteúdo:', error);
      setContentAvailable(false);
    }
  }, [location.pathname, connectionState.online, showTemporaryNotification]);
  
  // Pré-carregar assets para rotas alternativas quando offline
  const preloadAlternativeRoutes = useCallback(async () => {
    try {
      // Obter rotas de alta prioridade (por exemplo, Home e Ajuda)
      const highPriorityRoutes = routes
        .filter(route => route.priority === 'high')
        .map(route => route.path);
      
      // Pré-carregar assets dessas rotas
      if (highPriorityRoutes.length > 0) {
        for (const route of highPriorityRoutes) {
          await assetPreloader.preloadRouteAssets(route);
        }
      }
    } catch (error) {
      console.error('Erro ao pré-carregar rotas alternativas:', error);
    }
  }, [routes]);

  // Manipular mudanças no estado de conectividade
  const handleConnectivityChange = useCallback((newState) => {
    // Armazenar o estado anterior para comparação
    const wasOnline = connectionState.online;
    
    // Atualizar estado
    setConnectionState(newState);
    
    // Notificar usuário sobre mudanças na conectividade apenas se houve uma mudança real
    if (!newState.online && wasOnline) {
      // Acabou de ficar offline
      showTemporaryNotification('Você está offline. Usando conteúdo em cache.', 'warning');
    } else if (newState.online && !wasOnline) {
      // Acabou de ficar online - verificar se devemos mostrar notificação
      if (userPreferences.current.showOnlineNotifications) {
        showTemporaryNotification('Você está online novamente!', 'info');
      }
      
      // Verificar e sincronizar dados, se necessário
      if (newState.offlineDuration > 60) { // Se esteve offline por mais de 1 minuto
        // Atualizar cache em segundo plano
        assetPreloader.updateRouteCache(location.pathname);
      }
    }
    
    // Se estamos online, o conteúdo está "disponível" por definição
    if (newState.online) {
      setContentAvailable(true);
    } else {
      // Se estamos offline, checar se o conteúdo necessário está em cache
      checkContentAvailability();
    }
    
    // Atualizar referência de estado
    lastConnectivityNotification.current.online = newState.online;
  }, [connectionState.online, location.pathname, checkContentAvailability, showTemporaryNotification]);
  
  // Detectar pressionar triplo para alternar notificações online (Easter egg)
  const tapCount = useRef(0);
  const lastTapTime = useRef(0);
  
  useEffect(() => {
    // Função para manipular clique triplo na notificação
    const handleTripleTap = (event) => {
      const now = Date.now();
      
      // Resetar contador se passou muito tempo desde o último clique
      if (now - lastTapTime.current > 1000) {
        tapCount.current = 0;
      }
      
      tapCount.current += 1;
      lastTapTime.current = now;
      
      // Três cliques rápidos alternará a configuração
      if (tapCount.current === 3) {
        toggleOnlineNotifications();
        tapCount.current = 0;
      }
    };
    
    // Adicionar listener ao elemento da notificação quando visível
    const notification = document.querySelector('.offline-notification');
    if (notification) {
      notification.addEventListener('click', handleTripleTap);
      
      return () => {
        notification.removeEventListener('click', handleTripleTap);
      };
    }
  }, [showNotification, toggleOnlineNotifications]);
  
  // Efeito para monitorar conectividade e disponibilidade de conteúdo
  useEffect(() => {
    // Registrar para atualizações de conectividade
    const unsubscribe = connectivityService.subscribe(handleConnectivityChange);
    
    // Detectar tipo de conexão (se disponível na API)
    if (navigator.connection) {
      const updateConnectionQuality = () => {
        const connection = navigator.connection;
        
        // Para conexões lentas ou com limite de dados, otimizar o carregamento
        if (connection.saveData || 
            connection.effectiveType === 'slow-2g' || 
            connection.effectiveType === '2g') {
          
          // Adicionar classe para otimizações CSS (menos animações, imagens menores, etc)
          document.body.classList.add('data-saving-mode');
          
          // Notificar usuário se modo de economia de dados estiver ativo
          if (connection.saveData) {
            showTemporaryNotification('Modo de economia de dados ativo', 'info');
          }
        } else {
          document.body.classList.remove('data-saving-mode');
        }
      };

      // Verificar qualidade da conexão no início
      updateConnectionQuality();
      
      // Monitorar mudanças na conexão
      navigator.connection.addEventListener('change', updateConnectionQuality);
      
      // Limpeza
      return () => {
        navigator.connection.removeEventListener('change', updateConnectionQuality);
        unsubscribe();
      };
    } else {
      // API de Conexão não disponível, apenas verificar disponibilidade
      checkContentAvailability();
      
      // Limpar inscrição de conectividade ao desmontar
      return () => unsubscribe();
    }
  }, [location.pathname, handleConnectivityChange, checkContentAvailability, showTemporaryNotification]);
  
  // Se estamos offline e o conteúdo não está disponível, mostrar fallback
  if (!connectionState.online && !contentAvailable) {
    return (
      <OfflineFallback 
        message="Este conteúdo não está disponível offline."
        availableRoutes={routes.filter(route => route.priority === 'high')} 
      />
    );
  }
  
  // Renderizar conteúdo normal com notificação (se necessário)
  return (
    <>
      {children}
      
      {showNotification && (
        <div className={`offline-notification offline-notification-${notificationType}`}>
          <span className="notification-message">{notificationMessage}</span>
          <button 
            className="notification-close" 
            onClick={() => setShowNotification(false)}
            aria-label="Fechar notificação"
          >
            ×
          </button>
        </div>
      )}
    </>
  );
}

export default OfflineAwareWrapper; 